import { AssessmentPeriodWindowType, AssessmentPeriodItem } from "../../../hooks/useAssessmentPeriods";

export interface AssessmentWindow {
  activeDistrictWindow: boolean;
  previousWindow: boolean;
  upcomingWindow: boolean;
  activeAssessmentWindow: { startDate: string; endDate: string } | null;
  upcomingAssessmentWindow: { startDate: string; endDate: string } | null;
  previousAssessmentWindow: { startDate: string; endDate: string } | null;
  daysLeftInCurrentAssessmentWindow: number | null;
  daysUntilNextAssessmentWindowOpens: number | null;
  assessmentWindowType: string | null;
  currentPeriodIdentifier: number | null;
  tags: string[] | null;
  name: string | null;
}

export const findAssessmentWindow = (
  allEntries: AssessmentPeriodItem[]
): AssessmentWindow => {
  const now = new Date(); // Keep the actual current time for day calculations
  const today = new Date();
  today.setUTCHours(0, 0, 0, 0); // Keep this for date range comparisons

  const result: AssessmentWindow = {
    activeDistrictWindow: false,
    previousWindow: false,
    upcomingWindow: false,
    activeAssessmentWindow: null,
    upcomingAssessmentWindow: null,
    previousAssessmentWindow: null,
    daysLeftInCurrentAssessmentWindow: null,
    daysUntilNextAssessmentWindowOpens: null,
    assessmentWindowType: null,
    currentPeriodIdentifier: null,
    tags: null,
    name: null,
  };

  if (allEntries.length === 0) {
    return result;
  }

  const populateActiveWindowDetailsFromResult = (currentEntry: AssessmentPeriodItem, target: AssessmentWindow) => {
    target.activeAssessmentWindow = { startDate: currentEntry.startDate, endDate: currentEntry.endDate };
    target.daysLeftInCurrentAssessmentWindow = daysUntil(currentEntry.endDate, now);
    target.assessmentWindowType = currentEntry.assignmentType && currentEntry.assignmentType.length > 0 ? currentEntry.assignmentType : null;
    target.currentPeriodIdentifier = currentEntry.periodId;
    target.tags = currentEntry.tags;
    target.name = currentEntry.assignmentType;
  };

  // Stage 1: Try to find an active DISTRICT_ASSIGNMENT window
  const districtAssignmentEntries = allEntries.filter(
    (entry) =>
      entry.screeningWindowType === AssessmentPeriodWindowType.DISTRICT_ASSIGNMENT ||
      entry.screeningWindowType === AssessmentPeriodWindowType.SCHOOL_ASSIGNMENT
  );

  if (districtAssignmentEntries.length > 0) {
    const sortedDistrict = [...districtAssignmentEntries].sort(
      (a, b) => {
        // Parse dates in UTC for consistent timezone handling
        const [aYear, aMonth, aDay] = a.startDate.split('-').map(Number);
        const [bYear, bMonth, bDay] = b.startDate.split('-').map(Number);
        const aDate = new Date(Date.UTC(aYear, aMonth - 1, aDay));
        const bDate = new Date(Date.UTC(bYear, bMonth - 1, bDay));
        return aDate.getTime() - bDate.getTime();
      }
    );

    let currentDistrictIdx = -1;
    for (let i = 0; i < sortedDistrict.length; i++) {
      const { startDate, endDate } = sortedDistrict[i];
      // Parse dates in UTC for consistent timezone handling
      const [startYear, startMonth, startDay] = startDate.split('-').map(Number);
      const [endYear, endMonth, endDay] = endDate.split('-').map(Number);
      const start = new Date(Date.UTC(startYear, startMonth - 1, startDay));
      const end = new Date(Date.UTC(endYear, endMonth - 1, endDay));
      start.setUTCHours(0, 0, 0, 0);
      end.setUTCHours(23, 59, 59, 999);
      if (today >= start && today <= end) {
        currentDistrictIdx = i;
        break;
      }
    }

    if (currentDistrictIdx !== -1) {
      const curr = sortedDistrict[currentDistrictIdx];
      result.activeDistrictWindow = true;
      populateActiveWindowDetailsFromResult(curr, result);

      if (currentDistrictIdx > 0) {
        const prev = sortedDistrict[currentDistrictIdx - 1];
        result.previousWindow = true;
        result.previousAssessmentWindow = { startDate: prev.startDate, endDate: prev.endDate };
      }
      if (currentDistrictIdx < sortedDistrict.length - 1) {
        const next = sortedDistrict[currentDistrictIdx + 1];
        result.upcomingWindow = true;
        result.upcomingAssessmentWindow = { startDate: next.startDate, endDate: next.endDate };
        result.daysUntilNextAssessmentWindowOpens = daysUntil(next.startDate, now);
      }
      return result;
    }
  }

  // Stage 2: Find upcoming and previous DISTRICT_ASSIGNMENT windows without setting an active window
  const sortedDistrictOnly = [...districtAssignmentEntries].sort(
    (a, b) => {
      // Parse dates in UTC for consistent timezone handling
      const [aYear, aMonth, aDay] = a.startDate.split('-').map(Number);
      const [bYear, bMonth, bDay] = b.startDate.split('-').map(Number);
      const aDate = new Date(Date.UTC(aYear, aMonth - 1, aDay));
      const bDate = new Date(Date.UTC(bYear, bMonth - 1, bDay));
      return aDate.getTime() - bDate.getTime();
    }
  );

  // Find the nearest previous and upcoming district windows
  let previousDistrictWindow = null;
  let upcomingDistrictWindow = null;

  for (let i = sortedDistrictOnly.length - 1; i >= 0; i--) {
    const entry = sortedDistrictOnly[i];
    // Parse date in UTC for consistent timezone handling
    const [endYear, endMonth, endDay] = entry.endDate.split('-').map(Number);
    const end = new Date(Date.UTC(endYear, endMonth - 1, endDay));
    end.setUTCHours(23, 59, 59, 999);
    if (today > end) {
      previousDistrictWindow = entry;
      break;
    }
  }

  for (const entry of sortedDistrictOnly) {
    // Parse date in UTC for consistent timezone handling
    const [startYear, startMonth, startDay] = entry.startDate.split('-').map(Number);
    const start = new Date(Date.UTC(startYear, startMonth - 1, startDay));
    start.setUTCHours(0, 0, 0, 0);
    if (today < start) {
      upcomingDistrictWindow = entry;
      break;
    }
  }

  if (previousDistrictWindow) {
    result.previousWindow = true;
    result.previousAssessmentWindow = {
      startDate: previousDistrictWindow.startDate,
      endDate: previousDistrictWindow.endDate
    };
  }

  if (upcomingDistrictWindow) {
    result.upcomingWindow = true;
    result.upcomingAssessmentWindow = {
      startDate: upcomingDistrictWindow.startDate,
      endDate: upcomingDistrictWindow.endDate
    };
    result.daysUntilNextAssessmentWindowOpens = daysUntil(upcomingDistrictWindow.startDate, now);
  }

  // Stage 3: Check for current period in regular period entries
  const periodEntries = allEntries.filter(
    (entry) => entry.screeningWindowType === AssessmentPeriodWindowType.PERIOD
  );

  const sortedPeriods = [...periodEntries].sort(
    (a, b) => {
      // Parse dates in UTC for consistent timezone handling
      const [aYear, aMonth, aDay] = a.startDate.split('-').map(Number);
      const [bYear, bMonth, bDay] = b.startDate.split('-').map(Number);
      const aDate = new Date(Date.UTC(aYear, aMonth - 1, aDay));
      const bDate = new Date(Date.UTC(bYear, bMonth - 1, bDay));
      return aDate.getTime() - bDate.getTime();
    }
  );

  let currentPeriodEntry = null;
  for (const period of sortedPeriods) {
    // Parse dates in UTC for consistent timezone handling
    const [startYear, startMonth, startDay] = period.startDate.split('-').map(Number);
    const [endYear, endMonth, endDay] = period.endDate.split('-').map(Number);
    const start = new Date(Date.UTC(startYear, startMonth - 1, startDay));
    const end = new Date(Date.UTC(endYear, endMonth - 1, endDay));
    start.setUTCHours(0, 0, 0, 0);
    end.setUTCHours(23, 59, 59, 999);
    if (today >= start && today <= end) {
      currentPeriodEntry = period;
      break;
    }
  }

  if (currentPeriodEntry) {
    // Only set currentPeriodIdentifier and name from the period, not the active window
    result.currentPeriodIdentifier = currentPeriodEntry.periodId;
    result.name = currentPeriodEntry.assignmentType;
  }

  return result;
};

function daysUntil(dateStr: string, currentTime: Date): number {
  // Parse date in UTC for consistent timezone handling
  const [year, month, day] = dateStr.split('-').map(Number);
  const target = new Date(Date.UTC(year, month - 1, day)); // month is 0-indexed
  target.setUTCHours(0, 0, 0, 0);
  
  // Calculate the difference in milliseconds
  const diffMs = target.getTime() - currentTime.getTime();
  
  // If the target date has already passed, return 0
  if (diffMs <= 0) {
    return 0;
  }
  
  // Calculate days difference, rounding up to show partial days as full days
  const daysDiff = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
  return daysDiff;
}
